import React, { useState, useEffect, createContext, useContext } from 'react';
import { AuthProvider } from './contexts/AuthContext';
import { RevenueCatProvider } from './contexts/RevenueCatContext';
import AppRouter from './router/AppRouter';
import { CssBaseline } from '@mui/material';
import { LIGHT_THEME, DARK_THEME, COLORS, PRIMARY_ORANGE } from './common/colors';

// Theme types and interfaces
interface ThemeStyles {
  background: string;
  cardBackground: string;
  textPrimary: string;
  textSecondary: string;
  gradientText: string;
  inputBackground: string;
  chatUserBg: string;
  chatAssistantBg: string;
  borderColor: string;
  shadowColor: string;
  primary: string;
  chipBackground: string;
}

interface AppThemeContextType {
  isDarkMode: boolean;
  toggleTheme: () => void;
  getThemeStyles: () => ThemeStyles;
}

// Create theme context
const AppThemeContext = createContext<AppThemeContextType | undefined>(undefined);

// Custom hook to use theme
export const useAppTheme = () => {
  const context = useContext(AppThemeContext);
  if (!context) {
    throw new Error('useAppTheme must be used within App component');
  }
  return context;
};

// Theme styles function
const getThemeStyles = (isDarkMode: boolean): ThemeStyles => {
  if (isDarkMode) {
    return {
      background: DARK_THEME.background,
      cardBackground: DARK_THEME.cardBackground,
      textPrimary: DARK_THEME.textPrimary,
      textSecondary: DARK_THEME.textSecondary,
      gradientText: COLORS.primary,
      inputBackground: DARK_THEME.inputBackground,
      chatUserBg: COLORS.primary,
      chatAssistantBg: DARK_THEME.chatAssistantBg,
      borderColor: DARK_THEME.borderColor,
      shadowColor: DARK_THEME.shadowColor,
      primary: PRIMARY_ORANGE.main,
      chipBackground: 'rgba(60, 60, 60, 0.6)',
    };
  } else {
    return {
      background: LIGHT_THEME.background,
      cardBackground: LIGHT_THEME.cardBackground,
      textPrimary: LIGHT_THEME.textPrimary,
      textSecondary: LIGHT_THEME.textSecondary,
      gradientText: COLORS.primary,
      inputBackground: LIGHT_THEME.inputBackground,
      chatUserBg: COLORS.primary,
      chatAssistantBg: LIGHT_THEME.chatAssistantBg,
      borderColor: LIGHT_THEME.borderColor,
      shadowColor: LIGHT_THEME.shadowColor,
      primary: PRIMARY_ORANGE.main,
      chipBackground: 'rgba(229, 231, 235, 0.8)',
    };
  }
};

function App() {
  // Theme state management
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('darkMode');
    return saved ? JSON.parse(saved) : false;
  });

  useEffect(() => {
    localStorage.setItem('darkMode', JSON.stringify(isDarkMode));
  }, [isDarkMode]);

  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
  };

  const themeContextValue: AppThemeContextType = {
    isDarkMode,
    toggleTheme,
    getThemeStyles: () => getThemeStyles(isDarkMode),
  };

  return (
    <AppThemeContext.Provider value={themeContextValue}>
      <CssBaseline />
      <AuthProvider>
        <RevenueCatProvider>
          <AppRouter />
        </RevenueCatProvider>
      </AuthProvider>
    </AppThemeContext.Provider>
  );
}

export default App;
